import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode, type Ref } from "react";
import type { NavigationOutcome } from "../pageNavigation";
import { Modal } from "./ui/Modal";
import { Button } from "./ui/Button";
import "./AppNavigation.css";
import "./AppNavigationManagement.css";

type Follow = (event: ReactMouseEvent<HTMLAnchorElement> | undefined, path: string) => NavigationOutcome;

export function AppNavigation({ activeView, name, role, isAdmin, visibleViews, busy, onFollow, onOpenAccount, onLogout, notifications, placement = "top", accountOnly = false }: {
  name: string; role: string; isAdmin: boolean; busy: boolean;
  visibleViews: ReadonlySet<string>;
  activeView: string; onFollow: Follow; onLogout: () => Promise<boolean>; notifications?: ReactNode;
  /** 아바타를 누르면 계정 창이 열린다 — 페이지로 나가지 않으므로 보던 판이 남는다. */
  onOpenAccount: () => void;
  /** 화면 위 가로 막대로 설지, 왼쪽 세로 기둥으로 설지. */
  placement?: "top" | "rail";
  /** 판처럼 화면 전체를 쓰는 곳에서는 메뉴 없이 알림과 프로필만 띄운다 — 계정 창과
      로그아웃은 한 벌뿐이어야 하므로 이 컴포넌트를 그대로 쓴다. */
  accountOnly?: boolean;
}) {
  const [open, setOpen] = useState(false), [notice, setNotice] = useState("");
  const trigger = useRef<HTMLButtonElement>(null), brand = useRef<HTMLElement>(null);
  const account = useRef<HTMLDivElement>(null);
  const enteredPage = useRef(false), signingOut = useRef(false);
  useEffect(() => {
    const desktop = matchMedia("(min-width: 834px)");
    const update = () => { if (desktop.matches) setOpen(false); };
    desktop.addEventListener("change", update);
    return () => desktop.removeEventListener("change", update);
  }, []);
  const follow: Follow = (event, path) => {
    const outcome = onFollow(event, path);
    if (outcome === "accepted") { enteredPage.current = true; setOpen(false); setNotice(""); }
    else if (outcome === "blocked") setNotice("현재 화면과 입력을 유지했습니다.");
    return outcome;
  };
  const signOut = async () => {
    if (busy || signingOut.current) return;
    signingOut.current = true;
    try {
      if (await onLogout()) { enteredPage.current = true; setOpen(false); }
      else setNotice("현재 화면과 입력을 유지했습니다.");
    } finally { signingOut.current = false; }
  };
  const restore = () => enteredPage.current ? document.getElementById("main-content") : matchMedia("(min-width: 834px)").matches ? brand.current : trigger.current;
  const signOutButton = (compact = false) => <Button variant={compact ? "utility" : "secondary"} size={compact ? "compact" : "regular"} busy={busy} aria-label="로그아웃" onClick={() => void signOut()}>{busy ? "로그아웃 중" : "로그아웃"}</Button>;
  const primaryLinks = [
    /* 플래너는 당분간 메뉴에서 숨기되 직접 주소 접근은 유지한다. */
    { view: "planner", path: "/planner", label: "플래너", testId: "nav-planner", folded: true },
    { view: "work", path: "/work", label: "프로젝트", testId: "nav-work" },
    /* 잠시 접어 둔다. 사람의 정보·등록·삭제·문자는 작업현황이 모두 맡게 되었고, 남은 것은
       계약이 끝난 분들의 옛 기록뿐이라 매일 보는 메뉴에 설 자리가 아니다. 길(/studio-admin)은
       살려 두므로 주소를 치면 여전히 열린다. */
    { view: "workers", path: "/studio-admin", label: "작업자", testId: "nav-studio-admin", folded: true },
    { view: "settlement", path: "/settlement", label: "정산", testId: "nav-settlement" },
    { view: "ops", path: "/ops", label: "운영", testId: "nav-ops" },
    { view: "rack-overview", path: "/racks", label: "랙 현황", testId: "nav-racks" },
    /* 프로젝트 페이지는 주소로만 접근하는 기존 정책을 유지한다. */
    { view: "projects", path: "/projects", label: "프로젝트", testId: "nav-projects", folded: true },
  ];
  /* 세 겹이다 — 비치는 어두운 띠, 그 안의 홈, 홈 안의 고른 칸. 홈을 따로 두지 않으면
     "떠 있는 띠"와 "그중 하나를 고르는 자리"가 같은 면이 되어 위계가 사라진다. */
  const primaryNavigation = (className: string, ref?: typeof brand) => <nav ref={ref as Ref<HTMLElement>} className={className} aria-label="주요 메뉴">
    <span className="app-nav-trough">
      {primaryLinks.filter(link => !link.folded && visibleViews.has(link.view)).map(link => <a key={link.view} href={link.path} data-testid={link.testId}
        aria-current={activeView === link.view || link.view === "rack-overview" && activeView === "rack-status" ? "page" : undefined} aria-disabled={busy || undefined} tabIndex={busy ? -1 : undefined}
        onClick={event => { if (busy) { event.preventDefault(); return; } follow(event, link.path); }}>{link.label}</a>)}
    </span>
  </nav>;
  /* 메뉴 창에서는 계정으로 가는 길만 남는다 — 사용자 관리는 그 화면 안에 있다. */
  const accountLinks = <nav className="app-account-links" aria-label="계정 메뉴"><a href="/account" onClick={event => { follow(event, "/account"); }}>내 계정</a></nav>;
  return <>
    <a className="app-skip-link" href="#main-content">본문으로 이동</a>
    {/* 기둥으로 설 때는 가로 막대가 아니라 세로 열이다 — 메뉴가 위, 프로필·알림이 아래.
        같은 컴포넌트를 쓰는 이유는 메뉴·계정 창·로그아웃이 한 벌뿐이어야 하기 때문이다. */}
    <header className={accountOnly ? "app-global-floating" : placement === "rail" ? "app-global-rail" : "app-global-header"}>
      <div className={accountOnly ? "app-global-floating-inner" : placement === "rail" ? "app-global-rail-inner" : "app-global-inner"}>
        {!accountOnly && <button ref={trigger} type="button" className="app-menu-toggle" aria-label="메뉴 열기" aria-expanded={open} disabled={busy}
          onClick={() => { enteredPage.current = false; setNotice(""); setOpen(true); }}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 7h14M5 12h14M5 17h14" /></svg>
        </button>}
        {/* 워드마크를 걷어 두는 중이다. 넓은 화면에서 메뉴를 닫았을 때 포커스가 돌아갈
            자리를 그것이 맡고 있었으므로, 그 일은 첫 메뉴 항목을 담은 이 줄이 이어받는다. */}
        {!accountOnly && primaryNavigation("app-primary-navigation", brand)}
        <div className="app-global-account">
          {notifications}
          {/* The dismiss scope covers only this menu, so opening the notifications —
              which sit in the same row — closes the profile instead of overlapping it. */}
          <div className="app-account-menu" ref={account}>
            {/* 작은 창을 띄우지 않고 바로 들어간다 — 그 안에 있던 것(내 계정·사용자 관리·
                로그아웃)이 전부 계정 화면 한 판에 있다. 두 걸음 걸어 도착하던 자리를 한
                걸음으로 줄이고, 같은 것을 두 군데서 고르지 않게 한다. */}
            <button type="button" className="app-account-trigger" aria-label={`${name} 계정`}
              onClick={() => onOpenAccount()}>
              {/* 아바타에 이름 첫 글자가 든다 — 빈 동그라미는 "누구"를 말하지 못한다.
                  claude.ai/code 의 좌하단 프로필 줄을 재서 옮긴 모양이다
                  (docs/ui-comparisons/2026-10-16-claude-code-tile/spec.md). */}
              <span className="app-avatar" title={name} aria-hidden="true">{[...name][0] ?? ""}</span>
              {/* 판 위에서도 이름과 역할을 적는다. 점 하나만 떠 있으면 누구로 들어와 있는지
                  화면 어디에도 없다 — 여럿이 한 기계를 쓰는 자리에서 그게 가장 자주 샜다. */}
              {accountOnly && <span className="app-account-suffix">
                <span className="app-account-suffix-name">{name}</span>
                <span className="app-account-suffix-dot" aria-hidden="true">·</span>
                <span className="app-account-suffix-plan">{role}</span>
                <svg className="app-account-caret" viewBox="0 0 12 12" aria-hidden="true" width="12" height="12"
                  fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 4.5 6 7.5 9 4.5" />
                </svg>
              </span>}
              {/* 기둥에서는 이름과 역할을 함께 적는다 — 가로 막대와 달리 자리가 남고,
                  화면에 사람 이름이 하나도 없으면 누구로 들어와 있는지 알 수 없다. */}
              {placement === "rail" && <span className="app-account-identity"><strong>{name}</strong><span>{role}</span></span>}
            </button>
          </div>
        </div>
      </div>
    </header>
    {open && <Modal title="메뉴" className="app-menu-dialog" busy={busy} onClose={() => setOpen(false)} restoreFocus={restore}>
      {primaryNavigation("app-menu-primary")}
      <div className="app-menu-account"><div><strong>{name}</strong><span>{role}</span></div>{signOutButton()}</div>{accountLinks}
      {(notice || busy) && <p className="app-menu-notice" role="status">{busy ? "로그아웃을 처리하고 있습니다." : notice}</p>}
    </Modal>}
  </>;
}
